import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from './locales/en.json';
import es from './locales/es.json';
import ptBR from './locales/pt-BR.json';
import ru from './locales/ru.json';

/**
 * Languages.
 *
 * Four locales, with two rules from section 10 that are easy to get wrong:
 *
 *   1. **Spanish is the default**, not English.
 *   2. **Russian is hidden from the switcher unless the browser's own locale is
 *      Russian.** The reason is presentational honesty: the Russian build exists
 *      for the customer's own team, and offering it to a Brazilian user invites
 *      them into a half-empty translation. The server enforces the same rule
 *      when it returns the locale list, so the two cannot disagree.
 */

export const LOCALES = ['en', 'es', 'pt-BR', 'ru'] as const;
export type AppLocale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: AppLocale = 'es';

/** Locales that are always shown in the switcher. */
const ALWAYS_VISIBLE: readonly AppLocale[] = ['en', 'es', 'pt-BR'];

export const LOCALE_LABELS: Record<AppLocale, string> = {
  en: 'English',
  es: 'Español',
  'pt-BR': 'Português (BR)',
  ru: 'Русский',
};

/**
 * Map an arbitrary browser locale onto one we support.
 *
 * `pt-PT` and `pt` both mean Brazilian Portuguese here because that is the only
 * Portuguese variant the product has; `es-419` and `es-MX` both mean Spanish.
 */
export function normalizeLocale(input: string | null | undefined): AppLocale | null {
  if (!input) return null;
  const value = input.trim();
  if (!value) return null;

  // Exact match first, case-insensitively: `pt-BR` must not fall through to the
  // bare `pt` branch and lose its region.
  const exact = LOCALES.find((locale) => locale.toLowerCase() === value.toLowerCase());
  if (exact) return exact;

  const base = value.split('-')[0].toLowerCase();
  if (base === 'pt') return 'pt-BR';
  if (base === 'ru') return 'ru';
  if (base === 'es') return 'es';
  if (base === 'en') return 'en';
  return null;
}

/** The locale to start in, from `navigator.languages`. */
export function detectLocale(): AppLocale {
  if (typeof navigator === 'undefined') return DEFAULT_LOCALE;

  const candidates = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const candidate of candidates) {
    const normalized = normalizeLocale(candidate);
    if (normalized) return normalized;
  }

  return DEFAULT_LOCALE;
}

/** True when the browser's own language is Russian. */
export function browserPrefersRussian(): boolean {
  if (typeof navigator === 'undefined') return false;
  const candidates = navigator.languages?.length ? navigator.languages : [navigator.language];
  return candidates.some((candidate) => candidate?.toLowerCase().startsWith('ru'));
}

/**
 * Which locales a user may choose from.
 *
 * The chosen language is always offered, so a Russian speaker who has already
 * picked Russian never loses access to it just because they switched machines.
 */
export function visibleLocales(current?: string | null): AppLocale[] {
  const russianAvailable = browserPrefersRussian() || normalizeLocale(current) === 'ru';
  return LOCALES.filter(
    (locale) => locale !== 'ru' || russianAvailable,
  );
}

/** Persisted choice, so a returning visitor keeps their language. */
const STORAGE_KEY = 'foco.locale';

export function readStoredLocale(): AppLocale | null {
  try {
    return normalizeLocale(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    // Private mode can throw on access. The detected locale is a fine fallback.
    return null;
  }
}

export function storeLocale(locale: AppLocale): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Not being able to remember the choice is not worth breaking the page for.
  }
}

/**
 * Determine the startup locale **before React renders**.
 *
 * Section 10.1 requires no flash of the wrong language, so this runs in
 * `main.tsx` ahead of `createRoot` and the result is what i18next is initialised
 * with. Doing it inside a component would render English (the i18next default)
 * for one frame before switching.
 */
export function resolveStartupLocale(): AppLocale {
  return readStoredLocale() ?? detectLocale();
}

/**
 * i18next configuration.
 *
 * `resolveStartupLocale()` has already reduced the browser's preference list to
 * one of our four codes, so i18next only has to *load* that exact resource.
 *
 * `nonExplicitSupportedLngs` is deliberately **off**, and that is the fix for a
 * real bug rather than a preference. With it enabled, i18next walks up from
 * `pt-BR` to its base `pt`, finds neither, and then — because the resolution
 * happens before the fallback — settles on `es`. The header showed
 * `<html lang="pt-BR">` and a Portuguese `pricing.title`, while
 * `pricing.subtitle` was served from the Spanish catalogue: a half-Portuguese,
 * half-Spanish pricing page that no unit test noticed because both strings
 * exist. Resolution is now exact, and `normalizeLocale` is the single place that
 * maps `pt-PT`, `es-AR` and a bare `ru` onto a supported code.
 */
void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      es: { translation: es },
      'pt-BR': { translation: ptBR },
      ru: { translation: ru },
    },
    lng: resolveStartupLocale(),
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...LOCALES],
    nonExplicitSupportedLngs: false,
    load: 'currentOnly',
    interpolation: {
      // React escapes interpolated values itself; doing it here as well would
      // render visible `&amp;` in company names.
      escapeValue: false,
    },
    returnNull: false,
  });

/** Keep `<html lang>` correct, for screen readers and for hyphenation. */
export function applyDocumentLanguage(locale: string): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = locale;
}

applyDocumentLanguage(i18n.language);

i18n.on('languageChanged', (locale) => {
  applyDocumentLanguage(locale);

  const normalized = normalizeLocale(locale);
  if (normalized) storeLocale(normalized);

  // A currency or date rendered in the previous locale would otherwise stay on
  // screen until the next unrelated re-render.
  document.documentElement.dataset.locale = locale;
});

export default i18n;
